import { parseCheckoutSelectionItems } from "./checkout-input-schemas.ts";
import { getLockerverseMinimumCustomAmountCents } from "./custom-amount.ts";
import type {
  LockerverseCatalog,
  LockerverseCheckoutItem,
  LockerverseCheckoutLineItem,
  LockerverseCheckoutSelectionItem,
  LockerverseProduct,
} from "./types.ts";
import { LockerverseSdkError } from "./types.ts";

type ResolvedSelectionItem = {
  checkoutItem: LockerverseCheckoutItem;
  quantity: number;
  unitAmountCents: number;
};

export function assertCheckoutItemsAreAvailable(
  catalog: LockerverseCatalog,
  items: LockerverseCheckoutItem[]
) {
  const listingIdByProductId = new Map(
    catalog.products.map(({ id, listingId }) => [id, listingId])
  );
  const requestedByListingId = items.reduce((requested, item) => {
    const listingId = listingIdByProductId.get(item.productId);
    if (listingId) {
      requested.set(
        listingId,
        (requested.get(listingId) ?? 0) +
          ("quantity" in item ? (item.quantity ?? 1) : 1)
      );
    }
    return requested;
  }, new Map<string, number>());
  const listingIsSoldOut = catalog.availability.listings.some(
    ({ listingId, maxPurchasableQuantity, status }) =>
      requestedByListingId.has(listingId) &&
      (status === "sold_out" ||
        (maxPurchasableQuantity !== null &&
          (requestedByListingId.get(listingId) ?? 0) > maxPurchasableQuantity))
  );
  const sharedLimitIsSoldOut = catalog.availability.sharedLimits.some(
    ({ listingIds, remainingQuantity, status }) => {
      const requestedQuantity = listingIds.reduce(
        (total, listingId) =>
          total + (requestedByListingId.get(listingId) ?? 0),
        0
      );
      return (
        requestedQuantity > 0 &&
        (status === "sold_out" || requestedQuantity > remainingQuantity)
      );
    }
  );
  if (listingIsSoldOut || sharedLimitIsSoldOut) {
    throw new LockerverseSdkError({
      code: "total_calculation_failed",
      message: "This option is sold out.",
      operation: "calculate_total",
      reportable: false,
      serverCode: "PAYMENT_WIDGET_SALES_LIMIT_REACHED",
    });
  }
}

function resolveAmountItem(
  product: LockerverseProduct,
  amountCents: number
): ResolvedSelectionItem {
  if (
    product.pricingMode !== "custom" ||
    product.paymentOption !== "one-time" ||
    amountCents < getLockerverseMinimumCustomAmountCents(product)
  ) {
    throw new TypeError("Selected Product amount is invalid.");
  }
  return {
    checkoutItem: { amountCents, productId: product.id },
    quantity: 1,
    unitAmountCents: amountCents,
  };
}

function resolveFixedItem(
  product: LockerverseProduct,
  quantity: number
): ResolvedSelectionItem {
  if (product.pricingMode !== "fixed" || product.amountCents === null) {
    throw new TypeError("Selected Product quantity is invalid.");
  }
  if (product.paymentOption !== "one-time" && quantity !== 1) {
    throw new TypeError("Selected recurring Product is invalid.");
  }
  if (
    (product.minimumQuantity !== null && quantity < product.minimumQuantity) ||
    (product.maximumQuantity !== null && quantity > product.maximumQuantity) ||
    (product.inventoryQuantity !== null && quantity > product.inventoryQuantity)
  ) {
    throw new TypeError("Selected Product quantity is invalid.");
  }
  return {
    checkoutItem:
      product.paymentOption === "one-time"
        ? { productId: product.id, quantity }
        : { productId: product.id },
    quantity,
    unitAmountCents: product.amountCents,
  };
}

function resolveSelectionItem(
  catalog: LockerverseCatalog,
  product: LockerverseProduct,
  selection: LockerverseCheckoutSelectionItem
) {
  const selectedQuantity = "quantity" in selection ? selection.quantity : 1;
  const resolved =
    "amountCents" in selection
      ? resolveAmountItem(product, selection.amountCents)
      : resolveFixedItem(product, selectedQuantity);
  const { checkoutItem, quantity, unitAmountCents } = resolved;

  return {
    checkoutItem,
    lineItem: {
      acceptsDiscounts: product.acceptsDiscounts,
      eventId:
        product.fulfillmentType === "event_admission" && catalog.event
          ? catalog.event.id
          : null,
      fulfillmentType: product.fulfillmentType,
      listingId: product.listingId,
      listingSlug: product.slug,
      paymentOption: product.paymentOption,
      pricingMode: product.pricingMode,
      productId: product.id,
      productName: product.name,
      quantity,
      subtotalCents: unitAmountCents * quantity,
      unitAmountCents,
    } satisfies LockerverseCheckoutLineItem,
  };
}

export function resolveCheckoutSelection(
  catalog: LockerverseCatalog,
  input: LockerverseCheckoutSelectionItem[]
) {
  let selection = parseCheckoutSelectionItems(input);
  const shipping = catalog.widget.requiredShipping;
  if (shipping) {
    const shippingProduct = catalog.products.find(
      ({ id }) => id === shipping.productId
    );
    const needsShipping = selection.some((item) =>
      catalog.products.some(
        (product) =>
          product.slug === item.productSlug &&
          shipping.triggerProductIds.includes(product.id)
      )
    );
    selection = selection.filter(
      (item) => item.productSlug !== shippingProduct?.slug
    );
    if (needsShipping) {
      if (!shippingProduct) {
        throw new TypeError("Required shipping Product is unavailable.");
      }
      selection.push({ productSlug: shippingProduct.slug, quantity: 1 });
    }
  }
  const selectedProducts = new Set<string>();
  const resolved = selection.map((item) => {
    const product = catalog.products.find(
      (candidate) => candidate.slug === item.productSlug
    );
    if (!product) {
      throw new TypeError("Selected Product is unavailable.");
    }
    if (selectedProducts.has(product.id)) {
      throw new TypeError("Each Product can appear only once per checkout.");
    }
    selectedProducts.add(product.id);
    return resolveSelectionItem(catalog, product, item);
  });
  if (shipping?.additionalUnitAmountCents) {
    const triggerQuantity = resolved.reduce(
      (total, { lineItem }) =>
        total +
        (shipping.triggerProductIds.includes(lineItem.productId)
          ? lineItem.quantity
          : 0),
      0
    );
    const shippingLine = resolved.find(
      ({ lineItem }) => lineItem.productId === shipping.productId
    )?.lineItem;
    if (shippingLine) {
      const amountCents =
        shippingLine.unitAmountCents +
        Math.max(0, triggerQuantity - 1) * shipping.additionalUnitAmountCents;
      if (!Number.isSafeInteger(amountCents) || amountCents > 2_147_483_647) {
        throw new TypeError("Required shipping amount is invalid.");
      }
      shippingLine.unitAmountCents = amountCents;
      shippingLine.subtotalCents = amountCents;
    }
  }
  const paymentOption = resolved[0]?.lineItem.paymentOption;
  if (!paymentOption) {
    throw new TypeError("Select at least one Product.");
  }
  if (
    resolved.length > 1 &&
    (paymentOption !== "one-time" ||
      resolved.some(({ lineItem }) => lineItem.paymentOption !== paymentOption))
  ) {
    throw new TypeError("Recurring Products must be purchased separately.");
  }
  const items = resolved.map(({ checkoutItem }) => checkoutItem);
  assertCheckoutItemsAreAvailable(catalog, items);
  return {
    items,
    lineItems: resolved.map(({ lineItem }) => lineItem),
    paymentOption,
  };
}
